import React, { useState, useCallback, useEffect, useRef } from 'react';
import './FormRenderer.css';
import { submitForm, searchFormRecords, deleteFormRecord, validateFormData, checkFormReferences, fetchSelectOptions } from '../../services/FormsService';
import ConfirmModal from '../admin/ConfirmModal';
import SlideOutPanel from './SlideOutPanel';
import ReferenceDataDisplay from './ReferenceDataDisplay';
import SearchResultsDisplay from './SearchResultsDisplay';
import SlideSearchPanel from './SlideSearchPanel';
import LookupField from './LookupField';

/**
 * FormRenderer - JSON Schema 기반 동적 폼 렌더링 컴포넌트
 * 선택된 폼의 실제 입력 UI를 생성하고 제출/검색 기능 제공
 */
const FormRenderer = ({ 
  form,
  prefilledData = {},
  onSuccess,
  onError,
  onClose  // 폼 닫기 콜백 추가
}) => {
  // 상태 관리
  const [formData, setFormData] = useState(prefilledData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null); // 삭제용 원본 데이터 보관
  const [currentMode, setCurrentMode] = useState('insert'); // 'insert' | 'update' 모드 관리
  const [selectOptions, setSelectOptions] = useState({}); // 동적 select 옵션을 위한 상태
  
  // 검색 결과 슬라이드 패널 상태
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // 🎯 수정 애니메이션 상태 추가
  const [updateAnimationId, setUpdateAnimationId] = useState(null);
  const [formUpdateAnimation, setFormUpdateAnimation] = useState(false);

  // 🆕 checkbox_save 모드 상태 추가
  const [checkboxSaveData, setCheckboxSaveData] = useState([]); // 로드된 전체 데이터
  const [selectedCheckboxItems, setSelectedCheckboxItems] = useState([]); // 선택된 항목 ID 배열
  const [isSavingCheckboxSelection, setIsSavingCheckboxSelection] = useState(false);
  
  // 🆕 확인 모달 상태 추가
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');

  // 슬라이드 아웃 패널 상태 (FK 참조 확인용으로만 사용)
  const [showSlideOutPanel, setShowSlideOutPanel] = useState(false);
  const [referenceData, setReferenceData] = useState(null);
  const [isCheckingReferences, setIsCheckingReferences] = useState(false);
  
  
  // 자동 포커스를 위한 ref
  const firstInputRef = useRef(null);
  // 포커스 설정 여부를 추적하는 ref (한 번만 포커스하기 위해)
  const hasInitialFocusRef = useRef(false);
  // 초기 검색 완료 여부를 추적하는 ref
  const hasInitialSearchRef = useRef(false);

  // 미리 채워진 데이터 업데이트
  useEffect(() => {
    if (prefilledData && Object.keys(prefilledData).length > 0) {
      setFormData(prefilledData);
    }
  }, [prefilledData]);

  // 폼이 처음 렌더링될 때만 첫 번째 입력 필드에 자동 포커스
  useEffect(() => {
    if (form && firstInputRef.current && !hasInitialFocusRef.current) {
      // 약간의 지연을 두고 포커스 설정 (DOM이 완전히 렌더링된 후)
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
          hasInitialFocusRef.current = true; // 포커스 설정 완료 표시
        }
      }, 100);
    }
  }, [form]);

  // 🎯 폼이 변경될 때 모든 상태 초기화
  useEffect(() => {
    if (form?.form_id) {
      console.log('폼 변경 감지:', form.form_id, '- 상태 초기화 중...');

      // 포커스 플래그 리셋
      hasInitialFocusRef.current = false;
      // 초기 검색 플래그 리셋
      hasInitialSearchRef.current = false;

      // 🔄 검색 관련 상태 초기화
      setSearchResults([]);
      setShowSearchPanel(false);
      setSelectedRecord(null);
      setCurrentMode('insert');
      setUpdateAnimationId(null);

      // 🔄 checkbox_save 모드 상태 초기화
      setCheckboxSaveData([]);
      setSelectedCheckboxItems([]);

      // 🔄 폼 데이터 및 에러 초기화 (prefilledData는 별도 useEffect에서 처리)
      if (!prefilledData || Object.keys(prefilledData).length === 0) {
        setFormData({});
      }
      setErrors({});
      setMessage(null);

      // 🔄 모달 및 패널 상태 초기화
      setShowSlideOutPanel(false);
      setShowConfirmModal(false);
      setReferenceData(null);

      console.log('폼 상태 초기화 완료:', form.form_id);
    }
  }, [form?.form_id, prefilledData]);


  // data_source가 있는 필드들의 옵션 로드
  useEffect(() => {
    const loadSelectOptions = async () => {
      const options = {};

      if (form && form.form_schema && form.form_schema.properties) {
        for (const [fieldName, fieldSchema] of Object.entries(form.form_schema.properties)) {
          // ui_type이 select, checkbox_list, checkbox_table이고 data_source가 있는 경우
          if ((fieldSchema.ui_type === 'select' || fieldSchema.ui_type === 'checkbox_list' || fieldSchema.ui_type === 'checkbox_table') && fieldSchema.data_source) {
            const fetchedOptions = await fetchSelectOptions(fieldSchema.data_source);
            options[fieldName] = fetchedOptions;
          }
        }
      }

      setSelectOptions(options);
    };

    loadSelectOptions();
  }, [form]);

  // ESC 키 이벤트 핸들러 (검색 패널이 닫혀있을 때만 동작)
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && !showSearchPanel && !showSlideOutPanel && !showConfirmModal) {
        e.preventDefault();
        e.stopPropagation();
        
        // 다른 모달이나 패널이 열려있지 않을 때만 폼 초기화
        if (currentMode === 'update' || Object.keys(formData).length > 0) {
          // handleReset 로직을 직접 구현 (dependency 순환 문제 해결)
          setFormData({});
          setErrors({});
          setMessage({ 
            type: 'info', 
            text: '폼이 초기화되었습니다.' 
          });
          setSearchResults([]);
          setSelectedRecord(null);
          setCurrentMode('insert');
        } else {
          // 빈 폼에서 ESC: onClose 콜백이 있으면 바로 호출, 없으면 메시지 표시
          if (onClose) {
            onClose();
          } else {
            setMessage({ 
              type: 'info', 
              text: 'ESC 키를 눌렀습니다.' 
            });
          }
        }
      }
    };

    // 검색 패널, 슬라이드 아웃 패널, 확인 모달이 모두 닫혀있을 때만 이벤트 리스너 등록
    if (!showSearchPanel && !showSlideOutPanel && !showConfirmModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showSearchPanel, showSlideOutPanel, showConfirmModal, currentMode, formData, onClose]);

  // 폼 필드 값 변경 핸들러
  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // 해당 필드의 에러 제거
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [errors]);

  // **🎯 Lookup 필드 변경 핸들러 (관련 필드 자동 업데이트 포함)**
  const handleLookupFieldChange = useCallback((fieldName, value, selectedOption = null) => {
    console.log('FormRenderer: handleLookupFieldChange 호출됨');
    console.log('FormRenderer: fieldName:', fieldName);
    console.log('FormRenderer: value:', value);
    console.log('FormRenderer: selectedOption:', selectedOption);
    console.log('FormRenderer: 현재 formData:', formData);
    
    // 기본 필드 값 업데이트
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldName]: value
      };
      console.log('FormRenderer: 기본 필드 업데이트 후 formData:', newData);
      return newData;
    });
    
    // Lookup 의존성 처리 (선택된 옵션이 있을 때만)
    if (selectedOption && form.lookup_dependencies && form.lookup_dependencies[fieldName]) {
      const dependency = form.lookup_dependencies[fieldName];
      const triggeredFields = dependency.triggers || [];
      const mapping = dependency.mapping || {};
      
      console.log('FormRenderer: Lookup 의존성 처리 시작');
      console.log('FormRenderer: dependency:', dependency);
      console.log('FormRenderer: triggeredFields:', triggeredFields);
      console.log('FormRenderer: mapping:', mapping);
      
      setFormData(prev => {
        const updated = { ...prev, [fieldName]: value };
        
        // 트리거된 필드들 업데이트
        triggeredFields.forEach(triggerField => {
          if (mapping[triggerField]) {
            const sourceField = mapping[triggerField];
            const mappedValue = selectedOption[sourceField] || '';
            updated[triggerField] = mappedValue;
            console.log(`FormRenderer: ${triggerField} = ${mappedValue} (from ${sourceField})`);
          }
        });
        
        console.log('FormRenderer: 최종 업데이트된 formData:', updated);
        return updated;
      });
      
      console.log(`Lookup dependency triggered for ${fieldName}:`, {
        selectedOption,
        triggeredFields,
        mapping
      });
    } else {
      console.log('FormRenderer: Lookup 의존성 처리 안 함 - selectedOption:', !!selectedOption, 'dependencies:', !!form.lookup_dependencies);
    }
    
    // 해당 필드의 에러 제거
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [errors, form.lookup_dependencies, formData]);

  // 폼 리셋 핸들러
  const handleReset = useCallback(() => {
    setFormData({});
    setErrors({});
    setMessage(null);
    setSearchResults([]);
    setSelectedRecord(null); // 선택된 레코드도 초기화
    setCurrentMode('insert'); // INSERT 모드로 복귀
    
    // data_only_mode에서 폼 모드를 비활성화
    if (form.ui_config?.search_first?.data_only_mode) {
      setShowFormMode(false);
    }
  }, [form.ui_config?.search_first?.data_only_mode]);

  // 🆕 폼 표시 모드 상태 추가
  const [showFormMode, setShowFormMode] = useState(false);

  // "새로 추가" 버튼 핸들러
  const handleNewRecord = useCallback(() => {
    // kolon_user_management 폼일 때는 폼 모드를 활성화
    if (form.mode === 'search_first') {
      setShowFormMode(true);
      setCurrentMode('insert');
      setFormData({
        entity_code: 'KOLON_LSI', // 기본값 설정
        branch_code: 'HQ_KOLON',  // 기본값 설정
        role: 'user',             // 기본값 설정
        is_active: true           // 기본값 설정
      });
      setErrors({});
      setSelectedRecord(null);
      
      // 잠시 후 첫 번째 필드에 포커스
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
      return;
    }
    
    // 기존 동작 (일반 모드)
    setFormData({});
    setErrors({});
    setMessage({ 
      type: 'success', 
      text: '새 데이터 입력 모드로 전환되었습니다.' 
    });
    setSearchResults([]);
    setSelectedRecord(null);
    setCurrentMode('insert');
    
    // 첫 번째 필드에 포커스
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [form.mode]);

  // **🔧 폼 스키마 기반 필드 필터링 헬퍼 함수** (다른 함수들에서 사용되므로 앞에 정의)
  const filterFormFields = useCallback((data) => {
    // UPDATE 모드일 때는 update_form_schema도 고려
    const schemaToUse = (currentMode === 'update' && form?.update_form_schema)
      ? form.update_form_schema
      : form?.form_schema;

    if (!schemaToUse?.properties) {
      console.log('No form schema available, returning original data');
      return data;
    }

    const cleanData = {};
    const formFields = Object.keys(schemaToUse.properties);

    // 폼 스키마에 정의된 필드만 추출
    formFields.forEach(fieldName => {
      if (data.hasOwnProperty(fieldName)) {
        let value = data[fieldName];

        // datetime-local 필드의 timezone 정보 제거
        const fieldSchema = schemaToUse.properties[fieldName];
        if (fieldSchema?.format === 'datetime-local' && typeof value === 'string' && value.includes('+')) {
          // "2025-05-31T09:00:00+09:00" → "2025-05-31T09:00:00" 또는 "2025-05-31T09:00"
          value = value.split('+')[0];
          // 초가 있으면 제거 (datetime-local은 분까지만 지원)
          if (value.includes(':') && value.split(':').length === 3) {
            const parts = value.split(':');
            value = `${parts[0]}:${parts[1]}`;
          }
          console.log(`Datetime field ${fieldName}: ${data[fieldName]} → ${value}`);
        }

        cleanData[fieldName] = value;
      }
    });

    console.log('Original data:', data);
    console.log('Form schema fields:', formFields);
    console.log('Filtered data:', cleanData);
    return cleanData;
  }, [form, currentMode]);

  // FK 참조 확인 함수
  const checkFormReferencesForRecord = useCallback(async (record) => {
    setIsCheckingReferences(true);
    try {
      const result = await checkFormReferences(form.form_id, record);
      return result;
    } catch (error) {
      console.error('FK 참조 확인 오류:', error);
      setMessage({ 
        type: 'error', 
        text: '참조 데이터 확인 중 오류가 발생했습니다.' 
      });
      return null;
    } finally {
      setIsCheckingReferences(false);
    }
  }, [form]);

  // 삭제 핸들러 (FK 참조 확인 포함)
  const handleDelete = useCallback(async (recordToDelete = null) => {
    // 매개변수로 받은 record 또는 현재 selectedRecord 사용
    const targetRecord = recordToDelete || selectedRecord;
    
    // 선택된 레코드 확인
    if (!targetRecord) {
      setMessage({ 
        type: 'error', 
        text: '삭제할 데이터가 선택되지 않았습니다. 먼저 데이터를 검색하여 선택한 후 삭제해주세요.' 
      });
      return;
    }

    // 매개변수로 받은 record가 있으면 상태 업데이트
    if (recordToDelete) {
      setSelectedRecord(recordToDelete);
      setCurrentMode('update');
      const cleanData = filterFormFields(recordToDelete);
      setFormData(cleanData);
      setErrors({}); // 🔧 삭제 대상 선택 시 에러 상태 초기화
      setMessage({ 
        type: 'success', 
        text: '삭제할 데이터를 선택했습니다.' 
      });
    }

    // FK 참조 데이터 확인
    const referenceResult = await checkFormReferencesForRecord(targetRecord);
    if (!referenceResult) return;

    setReferenceData(referenceResult);

    // 참조 데이터가 있으면 슬라이드 아웃 패널로 표시
    if (referenceResult.total_references > 0) {
      setShowSlideOutPanel(true);
    } else {
      // 참조 데이터가 없으면 바로 삭제 확인
      const displayInfo = getRecordDisplayInfo(targetRecord);

      // 사용자 관리 폼인 경우 특별한 메시지 사용
      if (form.form_id === 'user_management') {
        const userName = targetRecord.user_name || targetRecord.user_identifier || '알 수 없는 사용자';
        setConfirmTitle('사용자 정보 삭제');
        setConfirmMessage(`사용자 : ${userName} 님을 삭제하시겠습니까?`);
      } else {
        // 기본 메시지
        setConfirmTitle('데이터 삭제');
        setConfirmMessage(`정말로 다음 데이터를 삭제하시겠습니까?\n\n${displayInfo}`);
      }
      setConfirmAction(() => async () => {
        await performDelete(targetRecord);
        setShowConfirmModal(false);
      });
      setShowConfirmModal(true);
    }
  }, [selectedRecord, checkFormReferencesForRecord, filterFormFields]);

  // 실제 삭제 수행 함수
  const performDelete = useCallback(async (targetRecord = null) => {
    // 매개변수로 받은 record 또는 selectedRecord 사용
    const recordToDelete = targetRecord || selectedRecord;
    
    // record null 체크
    if (!recordToDelete) {
      setMessage({ 
        type: 'error', 
        text: '삭제할 데이터가 선택되지 않았습니다.' 
      });
      return;
    }

    // 삭제에 필요한 ID 확인 (원본 레코드에서) - 더 많은 ID 필드 지원
    const deleteId = recordToDelete.user_id || recordToDelete.log_id || recordToDelete.assignment_id ||
                     recordToDelete.report_id || recordToDelete.company_id || recordToDelete.replacement_id ||
                     recordToDelete.maintenance_id || recordToDelete.technician_id || recordToDelete.asset_code || recordToDelete.id;
    
    if (!deleteId) {
      setMessage({ 
        type: 'error', 
        text: '삭제할 데이터의 ID가 없습니다. 데이터에 문제가 있을 수 있습니다.' 
      });
      return;
    }

    setIsDeleting(true);
    setMessage(null);

    try {
      // 원본 레코드의 모든 ID 정보 사용 (더 포괄적)
      const deleteData = {
        user_id: recordToDelete.user_id,
        log_id: recordToDelete.log_id,
        assignment_id: recordToDelete.assignment_id,
        report_id: recordToDelete.report_id,
        company_id: recordToDelete.company_id,
        replacement_id: recordToDelete.replacement_id,
        maintenance_id: recordToDelete.maintenance_id,
        technician_id: recordToDelete.technician_id,
        asset_code: recordToDelete.asset_code,
        id: recordToDelete.id
      };

      const result = await deleteFormRecord(form.form_id, deleteData);

      // 사용자 관리 폼인 경우 특별한 성공 메시지
      if (form.form_id === 'vehicle_user_management' || form.form_id === 'kolon_user_management') {
        const userName = recordToDelete.user_name || recordToDelete.user_identifier || '사용자';
        setMessage({
          type: 'success',
          text: `${userName} 님의 정보가 성공적으로 삭제되었습니다.`
        });
      } else {
        setMessage({
          type: 'success',
          text: '데이터가 성공적으로 삭제되었습니다.'
        });
      }
      
      // 성공 콜백 호출
      onSuccess?.(result);

      // 삭제 후 항상 폼 초기화
      setFormData({});
      setErrors({});
      setSelectedRecord(null);
      setCurrentMode('insert');

      // 🔄 search_first 모드에서는 목록 새로고침
      if (form.mode === 'search_first') {
        setTimeout(async () => {
          try {
            const searchResult = await searchFormRecords(form.form_id, {});
            if (searchResult.results) {
              setSearchResults(searchResult.results);
              setShowSearchPanel(true);
            }
          } catch (error) {
            console.error('검색 오류:', error);
          }
        }, 100);
      } else {
        // 일반 모드에서는 검색 결과에서 삭제된 항목 제거
        const deletedId = recordToDelete.user_id || recordToDelete.log_id || recordToDelete.assignment_id ||
                          recordToDelete.report_id || recordToDelete.company_id || recordToDelete.replacement_id ||
                          recordToDelete.maintenance_id || recordToDelete.technician_id || recordToDelete.asset_code || recordToDelete.id;

        if (deletedId) {
          setSearchResults(prev => prev.filter(item => {
            const itemId = item.user_id || item.log_id || item.assignment_id ||
                          item.report_id || item.company_id || item.replacement_id ||
                          item.maintenance_id || item.technician_id || item.asset_code || item.id;
            return itemId !== deletedId;
          }));
        }
      }
      
    } catch (error) {
      console.error('Form delete error:', error);
      const errorMessage = error.message || '데이터 삭제 중 오류가 발생했습니다.';
      setMessage({ type: 'error', text: errorMessage });
      onError?.(error);
    } finally {
      setIsDeleting(false);
    }
  }, [form, onSuccess, onError, handleReset]);

  // 레코드 표시 정보 추출 헬퍼 함수
  const getRecordDisplayInfo = useCallback((record) => {
    if (!record) return '알 수 없는 데이터';
    
    // 폼별로 적절한 표시 정보 생성
    const parts = [];
    
    // 차량 정보
    if (record.vehicle_identifier || record.car_number) {
      parts.push(`차량: ${record.vehicle_identifier || ''} ${record.car_number || ''}`.trim());
    }
    
    // 사용자 정보
    if (record.user_name || record.user_identifier) {
      parts.push(`사용자: ${record.user_name || record.user_identifier || ''}`.trim());
    }
    
    // 회사 정보
    if (record.company_name) {
      parts.push(`회사: ${record.company_name}`);
    }
    
    // 날짜 정보
    if (record.maintenance_date) {
      parts.push(`정비일: ${record.maintenance_date}`);
    }
    if (record.assigned_from_datetime) {
      parts.push(`할당일: ${new Date(record.assigned_from_datetime).toLocaleDateString()}`);
    }
    if (record.incident_datetime) {
      parts.push(`사고일: ${new Date(record.incident_datetime).toLocaleDateString()}`);
    }
    
    // ID 정보 (마지막에 추가)
    const id = record.user_id || record.log_id || record.assignment_id || 
               record.report_id || record.company_id || record.id;
    if (id) {
      parts.push(`ID: ${id}`);
    }
    
    return parts.length > 0 ? parts.join('\n') : '선택된 데이터';
  }, []);

  // 폼 제출 핸들러
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // **🔍 search_only 모드에서는 제출 차단**
    if (form.mode === 'search_only') {
      setMessage({ 
        type: 'error', 
        text: '조회 전용 폼입니다. 데이터 수정은 불가능합니다.' 
      });
      return;
    }
    
    // Primary Key 필드 찾기
    const primaryKey = selectedRecord && (
      selectedRecord.user_id || selectedRecord.log_id || selectedRecord.assignment_id ||
      selectedRecord.report_id || selectedRecord.company_id || selectedRecord.replacement_id ||
      selectedRecord.maintenance_id || selectedRecord.technician_id || selectedRecord.asset_code || selectedRecord.id
    );

    // 현재 모드에 따른 동작 결정
    const isUpdateMode = currentMode === 'update' && selectedRecord && primaryKey;

    // 제출할 데이터 준비
    let submitData = { ...formData };

    // UPDATE 모드면 Primary Key 포함, INSERT 모드면 제외
    if (isUpdateMode) {
      // 모든 가능한 Primary Key 포함
      if (selectedRecord.user_id) submitData.user_id = selectedRecord.user_id;
      if (selectedRecord.log_id) submitData.log_id = selectedRecord.log_id;
      if (selectedRecord.assignment_id) submitData.assignment_id = selectedRecord.assignment_id;
      if (selectedRecord.report_id) submitData.report_id = selectedRecord.report_id;
      if (selectedRecord.company_id) submitData.company_id = selectedRecord.company_id;
      if (selectedRecord.replacement_id) submitData.replacement_id = selectedRecord.replacement_id;
      if (selectedRecord.maintenance_id) submitData.maintenance_id = selectedRecord.maintenance_id;
      if (selectedRecord.technician_id) submitData.technician_id = selectedRecord.technician_id;
      if (selectedRecord.asset_code) submitData.asset_code = selectedRecord.asset_code;
      if (selectedRecord.id) submitData.id = selectedRecord.id;
      console.log('UPDATE 모드: Primary Key 포함', submitData);
    } else {
      // INSERT 모드: 폼 스키마에 정의된 필드만 사용
      submitData = filterFormFields(submitData);
      
      // 🆕 숨겨진 필드의 기본값 자동 추가
      if (form.form_schema?.properties) {
        Object.entries(form.form_schema.properties).forEach(([fieldName, fieldSchema]) => {
          if (fieldSchema.ui_hidden && fieldSchema.default !== undefined) {
            submitData[fieldName] = fieldSchema.default;
          }
        });
      }
      
      console.log('INSERT 모드: 폼 스키마 필드만 사용 (숨겨진 필드 기본값 포함)', submitData);
    }
    
    // 디버깅용 로그
    console.log('Current mode:', currentMode);
    console.log('Form submission mode:', isUpdateMode ? 'UPDATE' : 'INSERT');
    console.log('Form submission data:', submitData);

    // UPDATE 모드일 때는 update_form_schema 사용
    const schemaForValidation = (isUpdateMode && form.update_form_schema) ? form.update_form_schema : form.form_schema;
    console.log('Form schema required:', schemaForValidation.required);

    // 유효성 검증
    const validation = validateFormData(schemaForValidation, formData);
    console.log('Validation result:', validation);
    
    if (!validation.isValid) {
      // 에러 매핑 개선
      const errorMap = {};
      validation.errors.forEach(error => {
        // "user_name은(는) 필수 입력 항목입니다." -> "user_name"
        const match = error.match(/^(.+?)은\(는\)/);
        const fieldName = match ? match[1] : error.split(' ')[0];
        errorMap[fieldName] = error;
      });
      
      setErrors(errorMap);
      setMessage({ 
        type: 'error', 
        text: `입력 정보를 확인해주세요. 오류: ${validation.errors.join(', ')}` 
      });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const mode = isUpdateMode ? 'update' : 'insert';
      const result = await submitForm(form.form_id, mode, submitData);
      
      const operationType = result.operation_type || mode;
      const operationName = operationType === 'update' ? '수정' : '저장';
      
      setMessage({ 
        type: 'success', 
        text: result.message || `폼 데이터가 성공적으로 ${operationName}되었습니다.` 
      });
      setErrors({}); // 🔧 폼 제출 성공 시 에러 상태 초기화
      
      // 성공 콜백 호출
      onSuccess?.(result);
      
      // 🎯 UPDATE 성공 시 검색 결과 목록에서도 해당 항목 업데이트 + 애니메이션
      if (isUpdateMode && selectedRecord) {
        const updatedId = selectedRecord.user_id || selectedRecord.log_id || selectedRecord.assignment_id ||
                          selectedRecord.report_id || selectedRecord.company_id || selectedRecord.replacement_id ||
                          selectedRecord.maintenance_id || selectedRecord.technician_id || selectedRecord.asset_code || selectedRecord.id;
        
        if (updatedId) {
          // 💫 목록 애니메이션 트리거 (1초간 하이라이트)
          setUpdateAnimationId(updatedId);
          setTimeout(() => {
            setUpdateAnimationId(null);
          }, 1000);
          
          // 🎉 폼 수정 성공 애니메이션 트리거 (1.5초간)
          setFormUpdateAnimation(true);
          setTimeout(() => {
            setFormUpdateAnimation(false);
          }, 1500);
          setSearchResults(prev => prev.map(item => {
            const itemId = item.user_id || item.log_id || item.assignment_id ||
                          item.report_id || item.company_id || item.replacement_id ||
                          item.maintenance_id || item.technician_id || item.asset_code || item.id;
            
            if (itemId === updatedId) {
              // 기존 항목을 현재 폼 데이터로 업데이트하되,
              // readonly 필드들(JOIN된 데이터 등)은 기존 값 유지
              const updatedItem = { ...item };
              
              // 폼 스키마에 정의된 필드만 업데이트
              const formFields = Object.keys(form.form_schema.properties || {});
              formFields.forEach(fieldName => {
                if (formData[fieldName] !== undefined) {
                  updatedItem[fieldName] = formData[fieldName];
                }
              });
              
              console.log('검색 결과 목록에서 항목 업데이트:', { 
                before: item, 
                after: updatedItem 
              });
              
              return updatedItem;
            }
            return item;
          }));
          
          // selectedRecord도 업데이트 (다음 수정을 위해)
          setSelectedRecord(prev => {
            const updated = { ...prev };
            const formFields = Object.keys(form.form_schema.properties || {});
            formFields.forEach(fieldName => {
              if (formData[fieldName] !== undefined) {
                updated[fieldName] = formData[fieldName];
              }
            });
            return updated;
          });
        }
      }
      
      // 🔄 성공 후 목록으로 돌아가기 및 새로고침
      if (form.mode === 'search_first') {
        // search_first 모드: 목록으로 돌아가고 자동 새로고침
        // 먼저 상태 초기화
        setShowFormMode(false);  // 폼 모드 비활성화 (목록으로 돌아가기)
        setCurrentMode('insert'); // 모드 초기화
        setFormData({});          // 폼 데이터 초기화
        setSelectedRecord(null);  // 선택된 레코드 초기화

        // 상태 업데이트 후 검색 실행 (빈 조건으로 전체 검색)
        setTimeout(async () => {
          try {
            const result = await searchFormRecords(form.form_id, {});
            if (result.results && result.results.length > 0) {
              setSearchResults(result.results);
              setShowSearchPanel(true);
            }
          } catch (error) {
            console.error('검색 오류:', error);
          }
        }, 100);
      } else if (!isUpdateMode) {
        // 일반 INSERT 성공 시 리셋
        handleReset();
      }
      // UPDATE 성공 시에도 목록 새로고침이 필요한 경우
      else if (isUpdateMode && form.mode === 'search_first') {
        // 먼저 상태 초기화
        setShowFormMode(false);  // 폼 모드 비활성화 (목록으로 돌아가기)
        setCurrentMode('insert'); // 모드 초기화
        setFormData({});          // 폼 데이터 초기화
        setSelectedRecord(null);  // 선택된 레코드 초기화

        // 상태 업데이트 후 검색 실행 (빈 조건으로 전체 검색)
        setTimeout(async () => {
          try {
            const result = await searchFormRecords(form.form_id, {});
            if (result.results && result.results.length > 0) {
              setSearchResults(result.results);
              setShowSearchPanel(true);
            }
          } catch (error) {
            console.error('검색 오류:', error);
          }
        }, 100);
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      // 상세한 에러 메시지 생성
      let errorMessage = error.message || '데이터 저장 중 오류가 발생했습니다.';
      let errorDetails = null;
      
      if (error.details) {
        console.log('Error details:', error.details);
        
        // 개발 모드에서는 상세 정보 표시
        if (error.details.error_type || error.details.traceback) {
          errorDetails = {
            type: error.details.error_type,
            tenant: error.details.tenant_id,
            form: error.details.form_id,
            mode: error.details.mode
          };
          
          if (process.env.NODE_ENV === 'development' && error.details.traceback) {
            console.error('Backend traceback:', error.details.traceback);
            errorDetails.traceback = error.details.traceback;
          }
        }
        
        // 사용자에게 보여줄 메시지 개선
        if (error.details.error_message) {
          errorMessage = error.details.error_message;
        }
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage,
        details: errorDetails
      });
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, formData, selectedRecord, currentMode, onSuccess, onError, filterFormFields]);

  // 검색 핸들러 (슬라이드 아웃 패널 사용)
  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();

    setIsSearching(true);
    setMessage(null);
    setErrors({}); // 🔧 검색 시 에러 상태 초기화
    setSearchResults([]);

    try {
      // INSERT 모드에서는 빈 조건으로 검색 (전체 목록), UPDATE 모드에서는 formData 사용
      const searchData = currentMode === 'insert' ? {} : formData;
      const result = await searchFormRecords(form.form_id, searchData);
      
      if (result.results && result.results.length > 0) {
        setSearchResults(result.results);
        
        // **🎯 검색 결과가 있으면 항상 목록창 표시 (개수와 상관없이)**
        setShowSearchPanel(true);
      } else {
        setMessage({ type: 'error', text: '검색 조건에 맞는 데이터가 없습니다.' });
      }
      
    } catch (error) {
      console.error('Form search error:', error);
      
      // 백엔드에서 보내는 구체적인 에러 정보 추출
      let errorMessage = '검색 중 오류가 발생했습니다.';
      let errorDetails = null;
      
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.error || errorMessage;
        
        // details 정보가 있으면 포함
        if (errorData.details) {
          errorDetails = {
            type: 'search_error',
            tenant: error.response.data.tenant || 'unknown',
            form: form.form_id,
            mode: 'search',
            traceback: errorData.details
          };
        }
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage,
        details: errorDetails
      });
      onError?.(error);
    } finally {
      setIsSearching(false);
    }
  }, [form, formData, currentMode, onError, filterFormFields]);

  // 🆕 search_first 모드일 때 자동 검색 실행 (초기 로딩 시에만)
  useEffect(() => {
    if (form?.mode === 'search_first' && form?.ui_config?.search_first?.data_only_mode && !hasInitialSearchRef.current) {
      console.log('search_first data_only_mode 감지 - 초기 자동 검색 실행');
      // 약간의 지연을 두고 검색 실행 (폼 초기화 완료 후)
      const timer = setTimeout(() => {
        handleSearch();
        hasInitialSearchRef.current = true;
        console.log('초기 자동 검색 실행됨');
      }, 800); // 지연 시간을 800ms로 증가

      return () => clearTimeout(timer);
    }
  }, [form?.mode, form?.ui_config?.search_first?.data_only_mode, handleSearch]);

  // 🆕 showFormMode가 false로 변경될 때 데이터 다시 로드 (INSERT 모드가 아닐 때만)
  useEffect(() => {
    if (form?.ui_config?.search_first?.data_only_mode && !showFormMode && form?.mode === 'search_first' && currentMode !== 'insert') {
      const timer = setTimeout(() => {
        handleSearch();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showFormMode, form?.ui_config?.search_first?.data_only_mode, form?.mode, currentMode, handleSearch]);

  // **🎯 엔터 키 핸들러 (모드별 동작)**
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // **🔍 search_only 모드에서는 수정 기능 차단**
      const isSearchOnlyMode = form.mode === 'search_only';
      
      if (isSearchOnlyMode) {
        // search_only 모드: 항상 검색만 실행
        console.log('엔터 키: search_only 모드 - 검색 실행');
        handleSearch();
      } else {
        // 🔄 일반 모드: 현재 모드에 따라 다른 동작 수행
        if (currentMode === 'update') {
          // UPDATE 모드: 수정 버튼 클릭
          console.log('엔터 키: UPDATE 모드 - 수정 실행');
          handleSubmit(e);
        } else {
          // INSERT 모드: 검색 실행
          console.log('엔터 키: INSERT 모드 - 검색 실행');
          handleSearch();
        }
      }
    }
  }, [currentMode, handleSubmit, handleSearch, form.mode]);

  // 검색 결과 슬라이드 패널 핸들러들
  const handleCloseSearchPanel = useCallback(() => {
    setShowSearchPanel(false);
  }, []);

  // Lookup 모달이 열릴 때 검색 패널 닫기
  const handleLookupModalOpen = useCallback(() => {
    if (showSearchPanel) {
      setShowSearchPanel(false);
    }
  }, [showSearchPanel]);

  // 슬라이드 아웃 패널 핸들러들 (FK 참조 확인용)
  const handleCloseSlideOutPanel = useCallback(() => {
    setShowSlideOutPanel(false);
    setReferenceData(null);
  }, []);

  // 참조 데이터에서 삭제 확인
  const handleConfirmDeleteWithReferences = useCallback(async () => {
    setShowSlideOutPanel(false);
    await performDelete(selectedRecord);
  }, [performDelete, selectedRecord]);

  // 참조 데이터에서 삭제 취소
  const handleCancelDeleteWithReferences = useCallback(() => {
    setShowSlideOutPanel(false);
    setReferenceData(null);
    setMessage({ 
      type: 'info', 
      text: '삭제가 취소되었습니다.' 
    });
  }, []);

  // 검색 결과에서 레코드 선택
  const handleSelectFromSearch = useCallback((record) => {
    console.log('Selected search result:', record);
    setSelectedRecord(record);

    // **🔍 kolon_user_management 폼일 때 특별 처리**
    if (form.mode === 'search_first') {
      setShowFormMode(true);
      setCurrentMode('update');

      // UPDATE 모드용 스키마로 필드 필터링
      const schemaToUse = form.update_form_schema || form.form_schema;
      const cleanData = {};
      const formFields = Object.keys(schemaToUse.properties || {});
      formFields.forEach(fieldName => {
        if (record.hasOwnProperty(fieldName)) {
          cleanData[fieldName] = record[fieldName];
        }
      });

      // 🔧 select box를 위한 특별 매핑 (site_code -> group_name)
      if (record.site_code && schemaToUse.properties?.group_name?.ui_type === 'select') {
        cleanData.group_name = record.site_code;
      }

      setFormData(cleanData);
      setErrors({});

      // 잠시 후 첫 번째 필드에 포커스
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
      return;
    }

    // **🔍 search_only 모드에서는 조회 모드 유지**
    if (form.mode === 'search_only') {
      const cleanData = filterFormFields(record);
      setFormData(cleanData);
      setErrors({});
      setMessage({
        type: 'success',
        text: '조회 전용 모드: 선택한 데이터를 확인할 수 있습니다.'
      });
    } else if (form.mode === 'search_first') {
      // **🆕 search_first 모드: UPDATE 모드로 전환 (패널은 메인 화면이므로 닫지 않음)**
      setCurrentMode('update');

      // UPDATE 모드용 스키마로 필드 필터링
      const schemaToUse = form.update_form_schema || form.form_schema;
      const cleanData = {};
      const formFields = Object.keys(schemaToUse.properties || {});
      formFields.forEach(fieldName => {
        if (record.hasOwnProperty(fieldName)) {
          cleanData[fieldName] = record[fieldName];
        }
      });

      // 🔧 select box를 위한 특별 매핑 (site_code -> group_name)
      if (record.site_code && schemaToUse.properties?.group_name?.ui_type === 'select') {
        cleanData.group_name = record.site_code;
      }

      setFormData(cleanData);
      setErrors({});
      setMessage({
        type: 'success',
        text: '선택한 데이터를 수정할 수 있습니다.'
      });
    } else {
      // 일반 모드: UPDATE 모드로 전환
      setCurrentMode('update');

      // UPDATE 모드용 스키마로 필드 필터링
      const schemaToUse = form.update_form_schema || form.form_schema;
      const cleanData = {};
      const formFields = Object.keys(schemaToUse.properties || {});
      formFields.forEach(fieldName => {
        if (record.hasOwnProperty(fieldName)) {
          cleanData[fieldName] = record[fieldName];
        }
      });

      // 🔧 select box를 위한 특별 매핑 (site_code -> group_name)
      if (record.site_code && schemaToUse.properties?.group_name?.ui_type === 'select') {
        cleanData.group_name = record.site_code;
      }

      setFormData(cleanData);
      setErrors({});
      setMessage({
        type: 'success',
        text: '선택한 데이터를 폼에 불러왔습니다. (다른 데이터 선택 가능)'
      });
    }
  }, [filterFormFields, form]);

  // 검색 결과에서 레코드 삭제
  const handleDeleteFromSearch = useCallback(async (record) => {
    console.log('Delete from search - selected record:', record);

    // 검색 패널은 계속 열어둠 (다른 데이터 선택 가능)

    // record를 직접 매개변수로 전달하여 삭제 처리
    // (handleDelete 내부에서 폼 반영 + 삭제 확인 모달 순서로 처리됨)
    await handleDelete(record);
  }, [handleDelete]);

  // 🆕 checkbox_save 모드: 체크박스 토글 핸들러
  const handleCheckboxToggle = useCallback((itemId, keyField = 'alarm_id') => {

    // checkboxSaveData를 직접 수정
    setCheckboxSaveData(prev => {

      const updated = prev.map(item => {
        const currentId = item[keyField];
        const normalizedCurrentId = typeof currentId === 'string' ? parseInt(currentId, 10) : currentId;
        const normalizedItemId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;

        if (normalizedCurrentId === normalizedItemId) {
          // boolean 값으로 확실히 변환
          const newCheckboxValue = item.checkbox === true ||
                                 item.checkbox === 1 ||
                                 item.checkbox === '1' ||
                                 item.checkbox === 'true' ? false : true;

          return { ...item, checkbox: newCheckboxValue };
        }
        return item;
      });

      return updated;
    });

    // selectedCheckboxItems도 업데이트 (저장 시 사용)
    setSelectedCheckboxItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  }, []);

  // 🆕 checkbox_save 모드: 전체 선택/해제 핸들러
  const handleSelectAll = useCallback((checked) => {
    // checkboxSaveData의 모든 checkbox 필드를 업데이트
    setCheckboxSaveData(prev => {
      return prev.map(item => ({
        ...item,
        checkbox: checked
      }));
    });

    // selectedCheckboxItems도 업데이트
    if (checked) {
      // 강제로 alarm_id 사용
      const keyField = 'alarm_id';
      const allIds = checkboxSaveData.map(item => {
        // 타입을 통일 (숫자로)
        const id = item[keyField];
        return typeof id === 'string' ? parseInt(id, 10) : id;
      });
      setSelectedCheckboxItems(allIds);
    } else {
      setSelectedCheckboxItems([]);
    }
  }, [checkboxSaveData]);

  // 🆕 checkbox_save 모드: 저장 핸들러
  const handleSaveCheckboxSelection = useCallback(async () => {
    // 체크 해제된(차단할) 알람 ID 계산 - checkbox 필드 직접 사용
    // 강제로 alarm_id 사용
    const keyField = 'alarm_id';
    const blockedIds = checkboxSaveData
      .filter(item => {
        // checkbox가 false인 항목들 (차단할 알람)
        return item.checkbox === false ||
               item.checkbox === 0 ||
               item.checkbox === '0' ||
               item.checkbox === 'false';
      })
      .map(item => {
        const id = item[keyField];
        return typeof id === 'string' ? parseInt(id, 10) : id;
      });

    // 선택된 알람 개수 계산
    const selectedCount = checkboxSaveData.filter(item =>
      item.checkbox === true || item.checkbox === 1 || item.checkbox === '1' || item.checkbox === 'true'
    ).length;


    setIsSavingCheckboxSelection(true);
    setMessage(null);

    try {
      // 차단할 알람 ID들을 저장 (체크 해제된 항목들)
      const result = await submitForm(form.form_id, 'save', {
        blocked_alarm_ids: blockedIds
      });

      setMessage({
        type: 'success',
        text: result.message || `${selectedCount}개 알람 활성, ${blockedIds.length}개 알람 차단 설정이 저장되었습니다.`
      });

      onSuccess?.(result);
    } catch (error) {
      console.error('체크박스 선택 저장 오류:', error);
      setMessage({
        type: 'error',
        text: error.message || '저장 중 오류가 발생했습니다.'
      });
      onError?.(error);
    } finally {
      setIsSavingCheckboxSelection(false);
    }
  }, [form, selectedCheckboxItems, checkboxSaveData, onSuccess, onError]);

  // 검색 결과 클릭 시 폼에 데이터 채우기
  const handleSearchResultClick = useCallback((resultData) => {
    console.log('Selected search result:', resultData);
    // 원본 데이터 보관 (삭제용)
    setSelectedRecord(resultData);
    // UPDATE 모드로 전환
    setCurrentMode('update');
    // 폼 스키마에 정의된 필드만 사용
    const cleanData = filterFormFields(resultData);
    setFormData(cleanData);
    setErrors({}); // 🔧 검색 결과 클릭 시 에러 상태 초기화
    setSearchResults([]); // 검색 결과 숨기기
    setMessage({ 
      type: 'success', 
      text: '선택한 데이터를 수정 모드로 불러왔습니다. 수정 후 저장하세요.' 
    });
  }, [filterFormFields]);

  // 필드 타입별 입력 컴포넌트 렌더링
  const renderField = useCallback((fieldName, fieldSchema, isFirstField = false) => {
    const value = formData[fieldName] || '';
    const hasError = errors[fieldName];
    // UPDATE 모드일 때는 update_form_schema의 required를 사용, 아니면 form_schema 사용
    const schemaToUse = (currentMode === 'update' && form.update_form_schema) ? form.update_form_schema : form.form_schema;
    const isRequired = schemaToUse.required?.includes(fieldName);
    
    // 🔧 UPDATE 모드에서 readOnly 필드 확인
    const isReadOnlyInUpdate = currentMode === 'update' &&
                              form.readonly_in_update &&
                              form.readonly_in_update.includes(fieldName);

    // 🔍 검색에 사용되지 않는 필드 확인 (readonly_in_search)
    const isReadOnlyInSearch = form.readonly_in_search &&
                              form.readonly_in_search.includes(fieldName);

    // 전체 readOnly 여부 결정 (search_only 모드는 입력 가능해야 함)
    const isReadOnly = fieldSchema.readOnly || isReadOnlyInUpdate || isReadOnlyInSearch;

    // **🎯 Lookup 필드 처리**
    if (fieldSchema.ui_type === 'lookup') {
      return (
        <LookupField
          fieldName={fieldName}
          fieldConfig={fieldSchema}
          value={value}
          onChange={handleLookupFieldChange}
          lookupConfig={fieldSchema.lookup_config || {}}
          disabled={isReadOnly}
          error={hasError}
          placeholder={fieldSchema.description}
          className={isFirstField ? 'first-field' : ''}
          onModalOpen={handleLookupModalOpen}
        />
      );
    }

    // 기본 props (ref는 각 input 타입별로 개별 적용)
    const inputProps = {
      id: fieldName,
      name: fieldName,
      value: value,
      onChange: (e) => handleFieldChange(fieldName, e.target.value),
      onKeyDown: handleKeyDown, // **🎯 엔터 키 검색 추가**
      className: `form-input ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim(),
      readOnly: isReadOnly,
      disabled: isReadOnly
    };

    switch (fieldSchema.type) {
      case 'string':
        if (fieldSchema.format === 'date') {
          return (
            <input
              {...inputProps}
              type="date"
              className={`form-datetime-input ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
              ref={isFirstField ? firstInputRef : null}
            />
          );
        } else if (fieldSchema.format === 'datetime-local') {
          return (
            <input
              {...inputProps}
              type="datetime-local"
              className={`form-datetime-input ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
              ref={isFirstField ? firstInputRef : null}
            />
          );
        } else if (fieldSchema.ui_type === 'select' && selectOptions[fieldName]) {
          // data_source를 사용하는 동적 select
          return (
            <select
              {...inputProps}
              className={`form-select ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
              ref={isFirstField ? firstInputRef : null}
            >
              <option value="">선택해주세요</option>
              {selectOptions[fieldName].map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        } else if (fieldSchema.enum) {
          return (
            <select
              {...inputProps}
              className={`form-select ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
              ref={isFirstField ? firstInputRef : null}
            >
              <option value="">선택해주세요</option>
              {fieldSchema.enum.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        } else if (fieldSchema.title?.includes('설명') || fieldSchema.title?.includes('내용')) {
          return (
            <textarea
              {...inputProps}
              className={`form-textarea ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
              rows={4}
              placeholder={fieldSchema.description}
              ref={isFirstField ? firstInputRef : null}
            />
          );
        } else {
          return (
            <input
              {...inputProps}
              type="text"
              placeholder={fieldSchema.description}
              ref={isFirstField ? firstInputRef : null}
            />
          );
        }

      case 'number':
      case 'integer':
        return (
          <input
            {...inputProps}
            type="number"
            className={`form-number-input ${hasError ? 'error' : ''} ${isReadOnly ? 'readonly' : ''}`.trim()}
            placeholder={fieldSchema.description}
            ref={isFirstField ? firstInputRef : null}
          />
        );

      case 'boolean':
        return (
          <div className="form-checkbox-container">
            <input
              id={fieldName}
              name={fieldName}
              type="checkbox"
              checked={value === true}
              onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
              className="form-checkbox"
            />
            <label htmlFor={fieldName} className="form-checkbox-label">
              {fieldSchema.description || '예'}
            </label>
          </div>
        );

      case 'array':
        // checkbox_list 타입 처리
        if (fieldSchema.ui_type === 'checkbox_list' && selectOptions[fieldName]) {
          const selectedValues = Array.isArray(value) ? value : [];

          return (
            <div className="form-checkbox-list-container">
              {selectOptions[fieldName].map((option) => (
                <div key={option.value} className="form-checkbox-item">
                  <input
                    id={`${fieldName}_${option.value}`}
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...selectedValues, option.value]
                        : selectedValues.filter(v => v !== option.value);
                      handleFieldChange(fieldName, newValues);
                    }}
                    className="form-checkbox"
                    disabled={isReadOnly}
                  />
                  <label htmlFor={`${fieldName}_${option.value}`} className="form-checkbox-label">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          );
        }

        // checkbox_table 타입 처리
        if (fieldSchema.ui_type === 'checkbox_table' && selectOptions[fieldName]) {
          const selectedValues = Array.isArray(value) ? value : [];
          const displayColumns = fieldSchema.display_columns || [];

          return (
            <div className="form-checkbox-table-container">
              <table className="form-checkbox-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectOptions[fieldName].length > 0 && selectedValues.length === selectOptions[fieldName].length}
                        onChange={(e) => {
                          const newValues = e.target.checked
                            ? selectOptions[fieldName].map(opt => opt.value)
                            : [];
                          handleFieldChange(fieldName, newValues);
                        }}
                        disabled={isReadOnly}
                        title="전체 선택/해제"
                      />
                    </th>
                    {displayColumns.map((col) => (
                      <th key={col.field} style={{ width: col.width }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectOptions[fieldName].map((option) => (
                    <tr key={option.value} className={selectedValues.includes(option.value) ? 'selected' : ''}>
                      <td className="checkbox-column">
                        <input
                          id={`${fieldName}_${option.value}`}
                          type="checkbox"
                          checked={selectedValues.includes(option.value)}
                          onChange={(e) => {
                            const newValues = e.target.checked
                              ? [...selectedValues, option.value]
                              : selectedValues.filter(v => v !== option.value);
                            handleFieldChange(fieldName, newValues);
                          }}
                          disabled={isReadOnly}
                        />
                      </td>
                      {displayColumns.map((col) => (
                        <td key={col.field} style={{ width: col.width }}>
                          {option[col.field] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;

      default:
        return (
          <input
            {...inputProps}
            type="text"
            placeholder={fieldSchema.description}
            ref={isFirstField ? firstInputRef : null}
          />
        );
    }
  }, [formData, errors, form, currentMode, handleFieldChange, handleLookupFieldChange, handleKeyDown, handleLookupModalOpen]);

  // 🔄 자동 검색 실행 (데이터가 없을 때만, 초기 로딩 시에만)
  useEffect(() => {
    if (form?.mode === 'search_first' && (!searchResults || searchResults.length === 0) && !hasInitialSearchRef.current) {
      console.log('사용자 데이터가 없음 - 초기 자동 검색 실행');
      const timer = setTimeout(() => {
        handleSearch();
        hasInitialSearchRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [form, searchResults, handleSearch]);

  // checkboxSaveData 변경 감지 (디버그용)
  useEffect(() => {
    if (checkboxSaveData.length > 0) {
      const checkedCount = checkboxSaveData.filter(item =>
        item.checkbox === true || item.checkbox === 1
      ).length;
    }
  }, [checkboxSaveData]);

  // 🆕 checkbox_save 모드 자동 로드
  useEffect(() => {
    const loadCheckboxSaveData = async () => {
      // checkbox_save 모드이면 자동으로 데이터 로드
      if (form?.mode === 'checkbox_save' && !hasInitialSearchRef.current) {
        setIsSearching(true);
        try {
          const result = await searchFormRecords(form.form_id, {});
          if (result.results) {
            setCheckboxSaveData(result.results);
            hasInitialSearchRef.current = true; // 한 번만 로드

            // 체크박스 필드가 true인 항목들을 초기 선택 상태로 설정
            // 강제로 alarm_id 사용 (GOLFCART 알람용)
            const keyField = 'alarm_id';


            // checkbox가 true, 1, "true", "1" 등 다양한 형태일 수 있으므로 유연하게 처리
            const initialSelected = result.results
              .filter(item => {
                // truthy 값 체크 (true, 1, "true", "1" 등 모두 처리)
                return item.checkbox === true ||
                       item.checkbox === 1 ||
                       item.checkbox === '1' ||
                       item.checkbox === 'true';
              })
              .map(item => {
                // 타입을 명확히 하기 위해 숫자로 변환
                const id = item[keyField];
                return typeof id === 'string' ? parseInt(id, 10) : id;
              });

            console.log('📌 초기 선택된 항목들:', initialSelected);
            console.log('📌 초기 선택된 항목 수:', initialSelected.length);
            console.log('📌 전체 항목 수:', result.results.length);
            setSelectedCheckboxItems(initialSelected);
          }
        } catch (error) {
          console.error('❌ checkbox_save 데이터 로드 오류:', error);
          setMessage({ type: 'error', text: '데이터 로드 중 오류가 발생했습니다.' });
        } finally {
          setIsSearching(false);
        }
      }
    };

    loadCheckboxSaveData();
  }, [form?.mode, form?.form_id]);

  if (!form) {
    return (
      <div className="form-loading">
        <div>폼을 선택해주세요.</div>
      </div>
    );
  }

  // **🔍 폼 모드 확인**
  const isSearchOnlyMode = form.mode === 'search_only';
  const isSearchFirstMode = form.mode === 'search_first';
  const isCheckboxSaveMode = form.mode === 'checkbox_save';

  // 🆕 checkbox_save 모드 UI
  if (isCheckboxSaveMode) {
    // 강제로 alarm_id 사용 (GOLFCART 알람용)
    const keyField = 'alarm_id';


    // checkbox 필드를 제외한 displayFields만 사용
    const displayFields = (form?.display_fields?.list_view || [])
      .filter(field => field.field !== 'checkbox');

    // 모든 항목이 체크되어 있는지 확인
    const allSelected = checkboxSaveData.length > 0 &&
      checkboxSaveData.every(item =>
        item.checkbox === true || item.checkbox === 1 || item.checkbox === '1' || item.checkbox === 'true'
      );

    return (
      <div className="form-renderer-container checkbox-save-mode">
        {/* 타이틀 */}
        <div className="checkbox-save-header">
          <h2>{form.icon} {form.title}</h2>
          {form.description && <p className="form-description">{form.description}</p>}
        </div>

        {/* 선택 정보 및 저장 버튼 (테이블 위) */}
        <div className="checkbox-save-controls">
          <div className="selection-info">
            <span className="selected-count">
              선택된 항목: <strong>{
                checkboxSaveData.filter(item =>
                  item.checkbox === true || item.checkbox === 1 || item.checkbox === '1' || item.checkbox === 'true'
                ).length
              }</strong> / {checkboxSaveData.length}
            </span>
          </div>
          <div className="checkbox-save-actions">
            <button
              onClick={handleSaveCheckboxSelection}
              disabled={isSavingCheckboxSelection}
              className="save-button primary"
            >
              {isSavingCheckboxSelection ? (
                <>
                  <div className="button-spinner"></div>
                  <span>저장 중...</span>
                </>
              ) : (
                <>
                  <span className="button-icon">💾</span>
                  <span>{form.ui_config?.checkbox_save?.save_button_text || '선택한 알람 저장'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 테이블 */}
        <div className="checkbox-save-table-container">
          {isSearching ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>데이터를 불러오는 중...</p>
            </div>
          ) : checkboxSaveData.length === 0 ? (
            <div className="empty-state">
              <p>표시할 데이터가 없습니다.</p>
            </div>
          ) : (
            <table className="checkbox-save-table">
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="전체 선택/해제"
                    />
                  </th>
                  {displayFields.map((field) => (
                    <th key={field.field} style={{ width: field.width }}>
                      {field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {checkboxSaveData.map((item, index) => {
                  // ID 타입 통일 (숫자로)
                  const rawId = item[keyField];
                  const itemId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;

                  // checkbox 필드 직접 사용 - 실시간 업데이트 반영
                  const isSelected = item.checkbox === true ||
                                   item.checkbox === 1 ||
                                   item.checkbox === '1' ||
                                   item.checkbox === 'true';


                  return (
                    <tr key={itemId} className={isSelected ? 'selected' : ''}>
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxToggle(itemId, keyField)}
                        />
                      </td>
                      {displayFields.map((field) => {
                        const cellValue = item[field.field];

                        // 알람 타입(category) 필드에 색상 적용
                        if (field.field === 'category') {
                          // 실제 enum 값을 콘솔에 출력하여 확인
                          if (index === 0) {
                            console.log(`📍 실제 category enum 값: ${cellValue}`);
                          }

                          let alarmTypeClass = '';
                          const upperValue = cellValue ? cellValue.toUpperCase() : '';
                          const lowerValue = cellValue ? cellValue.toLowerCase() : '';

                          // 다양한 가능한 enum 값들에 대응
                          // 파란색 - 관리/정보 레벨
                          if (upperValue === 'INFO' ||
                              upperValue === 'INFORMATION' ||
                              upperValue === 'LOW' ||
                              upperValue === 'NORMAL' ||
                              upperValue === 'LEVEL1' ||
                              upperValue === '1' ||
                              cellValue === '관리' ||
                              cellValue === '관리 알람' ||
                              cellValue === '관리알람' ||
                              cellValue === '정보' ||
                              cellValue === '정보 알람' ||
                              cellValue === '낮음' ||
                              lowerValue.includes('관리') ||
                              lowerValue.includes('info')) {
                            alarmTypeClass = 'alarm-type-management';
                          }
                          // 주황색 - 주의/경고 레벨
                          else if (upperValue === 'WARNING' ||
                                   upperValue === 'CAUTION' ||
                                   upperValue === 'MEDIUM' ||
                                   upperValue === 'LEVEL2' ||
                                   upperValue === '2' ||
                                   cellValue === '주의' ||
                                   cellValue === '주의 알람' ||
                                   cellValue === '주의알람' ||
                                   cellValue === '경고' ||
                                   cellValue === '경고 알람' ||
                                   cellValue === '보통' ||
                                   lowerValue.includes('주의') ||
                                   lowerValue.includes('warning') ||
                                   lowerValue.includes('caution')) {
                            alarmTypeClass = 'alarm-type-caution';
                          }
                          // 빨간색 - 긴급/위험 레벨
                          else if (upperValue === 'CRITICAL' ||
                                   upperValue === 'ERROR' ||
                                   upperValue === 'URGENT' ||
                                   upperValue === 'HIGH' ||
                                   upperValue === 'DANGER' ||
                                   upperValue === 'LEVEL3' ||
                                   upperValue === '3' ||
                                   cellValue === '긴급' ||
                                   cellValue === '긴급 알람' ||
                                   cellValue === '긴급알람' ||
                                   cellValue === '위험' ||
                                   cellValue === '위험 알람' ||
                                   cellValue === '높음' ||
                                   lowerValue.includes('긴급') ||
                                   lowerValue.includes('critical') ||
                                   lowerValue.includes('urgent') ||
                                   lowerValue.includes('위험')) {
                            alarmTypeClass = 'alarm-type-urgent';
                          }

                          return (
                            <td key={field.field} style={{ width: field.width }}>
                              <span className={alarmTypeClass || 'alarm-type-default'}>
                                {cellValue || '-'}
                              </span>
                            </td>
                          );
                        }
                        // 타입별 렌더링
                        else if (field.type === 'boolean') {
                          const labels = field.boolean_labels || { true: 'ON', false: 'OFF' };
                          return (
                            <td key={field.field}>
                              <span className={`badge ${cellValue ? 'badge-success' : 'badge-default'}`}>
                                {cellValue ? labels.true : labels.false}
                              </span>
                            </td>
                          );
                        } else if (field.type === 'badge') {
                          const badgeColors = field.badge_colors || {};
                          const colorClass = badgeColors[cellValue] || 'default';
                          return (
                            <td key={field.field}>
                              <span className={`badge badge-${colorClass}`}>
                                {cellValue || '-'}
                              </span>
                            </td>
                          );
                        } else if (field.type === 'badges') {
                          // 콤마로 구분된 값들을 개별 배지로 표시
                          const separator = field.separator || ', ';
                          const badgeColor = field.badge_color || 'primary';
                          const values = cellValue ? cellValue.split(separator).filter(v => v.trim()) : [];

                          return (
                            <td key={field.field} style={{ width: field.width }}>
                              <div className="badges-container">
                                {values.length > 0 ? (
                                  values.map((value, index) => (
                                    <span key={index} className={`badge badge-${badgeColor}`}>
                                      {value.trim()}
                                    </span>
                                  ))
                                ) : (
                                  <span className="no-data">-</span>
                                )}
                              </div>
                            </td>
                          );
                        } else {
                          return (
                            <td key={field.field} style={{ width: field.width }}>
                              {cellValue !== null && cellValue !== undefined ? cellValue : '-'}
                            </td>
                          );
                        }
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // 🎯 KOLON_LSI 사용자 관리 전용 UI
  if (form.mode === 'search_first') {
    // showFormMode가 활성화되면 폼 UI 표시
    if (showFormMode) {
      return (
        <div className="form-renderer-container">
          {/* 폼 헤더 */}
          <div className="form-header">
            <h2>📝 {currentMode === 'insert' ? '추가' : '수정'}</h2>
            <button
              onClick={() => {
                setShowFormMode(false);
                setCurrentMode('insert');
                setFormData({});
                setSelectedRecord(null);
                setErrors({});
              }}
              className="back-button"
            >
              ← 목록으로 돌아가기
            </button>
          </div>

          {/* 폼 액션 버튼들 */}
          <div className="form-action-toolbar">
            <div className="action-group primary-actions">
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`toolbar-button primary ${currentMode === 'update' ? 'update' : 'insert'}`}
                title={currentMode === 'update' ? '데이터 수정' : '데이터 저장'}
              >
                {isSubmitting ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>{currentMode === 'update' ? '수정 중' : '저장 중'}</span>
                  </>
                ) : (
                  <>
                    <span className="button-icon">{currentMode === 'update' ? '✏️' : '💾'}</span>
                    <span className="button-text">{currentMode === 'update' ? '수정' : '저장'}</span>
                  </>
                )}
              </button>

              {currentMode === 'update' && form.ui_config?.show_delete_in_update === true && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting || isSearching || isDeleting || !selectedRecord}
                  className="toolbar-button danger"
                  title="사용자 삭제"
                >
                  {isDeleting ? (
                    <>
                      <div className="button-spinner"></div>
                      <span>삭제 중</span>
                    </>
                  ) : (
                    <>
                      <span className="button-icon">🗑️</span>
                      <span className="button-text">삭제</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* 폼 입력 영역 */}
          <div className="form-input-container">
            <form onSubmit={handleSubmit} className="dynamic-form">
              {(() => {
                // UPDATE 모드일 때는 update_form_schema 사용, 아니면 form_schema 사용
                const schemaToUse = (currentMode === 'update' && form.update_form_schema) ? form.update_form_schema : form.form_schema;
                return schemaToUse?.properties && Object.entries(schemaToUse.properties)
                  .filter(([fieldName, fieldSchema]) => {
                    // ui_hidden 필드 제외
                    if (fieldSchema.ui_hidden) return false;
                    return true;
                  })
                  .map(([fieldName, fieldSchema], index) => {
                    // checkbox_table 타입이면 wide-field 클래스 추가
                    const isWideField = fieldSchema.ui_type === 'checkbox_table';
                    return (
                      <div key={fieldName} className={`form-group ${isWideField ? 'wide-field' : ''}`}>
                        <label htmlFor={fieldName} className="form-label">
                          {fieldSchema.title}
                          {schemaToUse.required?.includes(fieldName) && <span className="required">*</span>}
                        </label>
                        <div className="form-input">
                          {renderField(fieldName, fieldSchema, index === 0)}
                          {errors[fieldName] && <div className="error-message">{errors[fieldName]}</div>}
                        </div>
                      </div>
                    );
                  });
              })()}
            </form>
          </div>

          {/* 메시지 */}
          {message && (
            <div className={`form-message ${message.type}`}>
              <div className="message-text">{message.text}</div>
            </div>
          )}

          {/* 확인 모달 */}
          <ConfirmModal
            isOpen={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={confirmAction}
            title={confirmTitle}
            message={confirmMessage}
            type="warning"
          />
        </div>
      );
    }

    // 기본 목록 UI
    return (
      <div className="form-renderer-container search-first-empty">
        {/* 사용자 목록 영역 */}
        <div className="empty-ui-content">
          <div className="user-list-container">
            <div className="user-list-header">
              <h3>목록</h3>
              <span className="user-count">
                {isSearching ? '조회 중...' : `총 ${searchResults?.length || 0} 행`}
              </span>
              <div className="user-list-actions">
                {/* 🔄 수동 새로고침 버튼 추가 */}
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="action-button"
                >
                  🔄 새로고침
                </button>
                {form.ui_config?.search_first?.show_add_button !== false && (
                  <button
                    type="button"
                    onClick={handleNewRecord}
                    disabled={isSubmitting || isSearching || isDeleting}
                    className="action-button primary"
                  >
                    <span className="button-icon">➕</span>
                    <span className="button-text">추가하기</span>
                  </button>
                )}
              </div>
            </div>
            <div className="user-list-table">
              <SearchResultsDisplay
                results={searchResults || []}
                displayFields={form.display_fields?.search_result || []}
                onEdit={handleSelectFromSearch}
                onDelete={form.ui_config?.search_first?.row_actions?.some(action => action.type === 'delete') ? handleDeleteFromSearch : null}
                isLoading={isSearching}
              />
            </div>
          </div>
        </div>

        {/* 메시지 */}
        {message && (
          <div className={`form-message ${message.type}`}>
            <div className="message-text">{message.text}</div>
          </div>
        )}

        {/* 확인 모달 */}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmAction}
          title={confirmTitle}
          message={confirmMessage}
          type="warning"
        />
      </div>
    );
  }

  return (
    <div className="form-renderer-container">
      {/* 폼 모드 안내 메시지 */}
      {isSearchOnlyMode && (
        <div className="form-mode-notice">
          <span className="mode-icon">📋</span>
          <span className="mode-text">조회 전용 폼입니다. 데이터 수정은 불가능합니다.</span>
        </div>
      )}

      {/* 액션 툴바 */}
      <div className="form-action-toolbar">
        {/* data_only_mode일 때는 추가하기 버튼만 상단에 표시 */}
        {isSearchFirstMode && form.ui_config?.search_first?.data_only_mode && form.ui_config?.search_first?.show_add_button !== false && !showFormMode ? (
          <div className="action-group center-add-button">
            <button
              type="button"
              onClick={handleNewRecord}
              disabled={isSubmitting || isSearching || isDeleting}
              className="toolbar-button primary large"
              title={form.ui_config?.search_first?.add_button_text || "새 데이터 입력"}
            >
              <span className="button-icon">➕</span>
              <span className="button-text">{form.ui_config?.search_first?.add_button_text || "새로 추가"}</span>
            </button>
          </div>
        ) : (
          <>
            {/* 폼 모드일 때 기본 액션 버튼들 */}
            <div className="action-group primary-actions">
              {/* search_only 모드가 아닌 경우에만 수정/저장 버튼 표시 */}
              {!isSearchOnlyMode && (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`toolbar-button primary ${currentMode === 'update' ? 'update' : 'insert'}`}
                  title={currentMode === 'update' ? '데이터 수정' : '데이터 저장'}
                >
                  {isSubmitting ? (
                    <>
                      <div className="button-spinner"></div>
                      <span>{currentMode === 'update' ? '수정 중' : '저장 중'}</span>
                    </>
                  ) : (
                    <>
                      <span className="button-icon">{currentMode === 'update' ? '✏️' : '💾'}</span>
                      <span className="button-text">{currentMode === 'update' ? '수정' : '저장'}</span>
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={handleSearch}
                disabled={isSearching}
                className="toolbar-button search"
                title="데이터 검색"
              >
                {isSearching ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>검색 중</span>
                  </>
                ) : (
                  <>
                    <span className="button-icon">🔍</span>
                    <span className="button-text">검색</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleReset}
                disabled={isSubmitting || isSearching || isDeleting}
                className="toolbar-button reset"
                title={isSearchOnlyMode ? '폼 초기화' : (currentMode === 'update' ? '취소하고 초기화' : '폼 초기화')}
              >
                <span className="button-icon">🔄</span>
                <span className="button-text">초기화</span>
              </button>
            </div>

            {/* 두 번째 줄: 추가 액션 (폼 모드에서만) */}
            {currentMode === 'update' && !isSearchOnlyMode && form.ui_config?.show_delete_in_update === true && (
              <div className="action-group search-actions">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting || isSearching || isDeleting || !selectedRecord}
                  className="toolbar-button danger"
                  title="데이터 삭제"
                >
                  {isDeleting ? (
                    <>
                      <div className="button-spinner"></div>
                      <span>삭제 중</span>
                    </>
                  ) : (
                    <>
                      <span className="button-icon">🗑️</span>
                      <span className="button-text">삭제</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 메시지 */}
      {message && (
        <div className={`form-message ${message.type}`}>
          <div className="message-text">{message.text}</div>
          {message.details && process.env.NODE_ENV === 'development' && (
            <details className="error-details">
              <summary>🔍 개발자 정보 (클릭하여 펼치기)</summary>
              <div className="error-details-content">
                {message.details.type && (
                  <div><strong>에러 유형:</strong> {message.details.type}</div>
                )}
                {message.details.tenant && (
                  <div><strong>테넌트:</strong> {message.details.tenant}</div>
                )}
                {message.details.form && (
                  <div><strong>폼 ID:</strong> {message.details.form}</div>
                )}
                {message.details.mode && (
                  <div><strong>모드:</strong> {message.details.mode}</div>
                )}
                {message.details.traceback && (
                  <pre className="traceback">{message.details.traceback}</pre>
                )}
              </div>
            </details>
          )}
        </div>
      )}

      {/* 🆕 search_first 모드에서 검색 결과 표시 (항상 표시) */}
      {isSearchFirstMode && form.ui_config?.search_first?.data_only_mode && !showFormMode && (
        <div className="search-first-results-container">
          <div className="search-results-header">
            <h3>사용자 목록</h3>
            <span className="result-count">
              {isSearching ? '조회 중...' : `총 ${searchResults?.length || 0}개`}
            </span>
          </div>
          <div className="search-results-table-container">
            <SearchResultsDisplay
              results={searchResults || []}
              displayFields={form.display_fields?.search_result || []}
              onEdit={handleSelectFromSearch}
              onDelete={form.ui_config?.search_first?.row_actions?.some(action => action.type === 'delete') && form.mode !== 'search_only' ? handleDeleteFromSearch : null}
              isLoading={isSearching}
            />
          </div>
        </div>
      )}

      {/* 폼 입력 영역 - search_first 모드에서 data_only_mode일 때는 showFormMode가 활성화되어야만 표시 */}
      {(!(isSearchFirstMode && form.ui_config?.search_first?.data_only_mode) || showFormMode) &&
       !(isSearchFirstMode && form.ui_config?.search_first?.hide_form_inputs && !showFormMode) && (
        <div className={`form-input-container ${isSearchFirstMode ? 'search-first-form' : ''}`}>
          <form onSubmit={handleSubmit} className="dynamic-form">
            {(() => {
              // UPDATE 모드일 때는 update_form_schema 사용, 아니면 form_schema 사용
              const schemaToUse = (currentMode === 'update' && form.update_form_schema) ? form.update_form_schema : form.form_schema;
              return schemaToUse?.properties && Object.entries(schemaToUse.properties)
                .filter(([fieldName, fieldSchema]) => {
                  // ui_hidden 필드 제외
                  if (fieldSchema.ui_hidden) return false;
                  return true;
                })
                .map(([fieldName, fieldSchema], index) => {
                  // checkbox_table 타입이면 wide-field 클래스 추가
                  const isWideField = fieldSchema.ui_type === 'checkbox_table';
                  return (
                    <div key={fieldName} className={`form-group ${isWideField ? 'wide-field' : ''}`}>
                      <label htmlFor={fieldName} className="form-label">
                        {fieldSchema.title}
                        {schemaToUse.required?.includes(fieldName) && <span className="required">*</span>}
                      </label>
                      <div className="form-input">
                        {renderField(fieldName, fieldSchema, index === 0)}
                        {errors[fieldName] && <div className="error-message">{errors[fieldName]}</div>}
                      </div>
                    </div>
                  );
                });
            })()}
          </form>
        </div>
      )}

      {/* 슬라이드 검색 패널 (기존 모드용) */}
      {!isSearchFirstMode && (
        <SlideSearchPanel
          isOpen={showSearchPanel}
          onClose={() => setShowSearchPanel(false)}
          results={searchResults}
          displayFields={form.display_fields?.search_result || []}
          onEdit={handleSelectFromSearch}
          onDelete={form.mode !== 'search_only' ? handleDeleteFromSearch : null}
          isLoading={isSearching}
          title="검색 결과"
          selectedRecordId={updateAnimationId}
          updateAnimationId={updateAnimationId}
          isSearchOnly={isSearchOnlyMode}
        />
      )}

      {/* 확인 모달 */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        type="warning"
      />
    </div>
  );
}

export default FormRenderer;